import React, { useEffect } from "react";
import Modal from "react-modal";
import { ReactComponent as CloseModalSvg } from "../../../../assets/icons/x-icon.svg";
import styles from "./AccessibleDialog.module.scss";

// Shared accessible dialog wrapper (originally built for the ITAM-2583
// consult-order modals, Q17).
//
// Built on react-modal (which already provides focus trapping + focus restoration
// via shouldReturnFocusAfterClose) and adds the pieces react-modal does NOT give
// for free:
//  - a required dialog NAME (aria-labelledby via titleId, or aria-label),
//  - a close button with an explicit accessible name,
//  - defined INITIAL focus (initialFocusRef, focused on open),
//  - draft-preserving Escape/overlay (onRequestClose just closes; it never
//    discards the caller's draft — that is the caller's decision).

export interface AccessibleDialogProps {
  isOpen: boolean;
  onRequestClose: () => void;
  /** Id of the heading element -> aria-labelledby (preferred). */
  titleId?: string;
  /** Fallback accessible name when there is no visible heading to reference. */
  ariaLabel?: string;
  /** Element focused when the dialog opens. */
  initialFocusRef?: React.RefObject<HTMLElement>;
  /** Accessible name for the close button. */
  closeButtonLabel?: string;
  showCloseButton?: boolean;
  shouldCloseOnEsc?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  /** Class applied to the inner content wrapper. */
  className?: string;
  maxWidth?: number | string;
  /**
   * Overlay stacking level. Default 1000 (above page content, below rsuite
   * portals). Dialogs that must beat the FloatingVideoPanel wrapper (z 9998)
   * pass a higher value, e.g. the end-call confirm at 10000.
   */
  overlayZIndex?: number;
  children: React.ReactNode;
}

const AccessibleDialog: React.FC<AccessibleDialogProps> = ({
  isOpen,
  onRequestClose,
  titleId,
  ariaLabel,
  initialFocusRef,
  closeButtonLabel = "Zatvori",
  showCloseButton = true,
  shouldCloseOnEsc = true,
  shouldCloseOnOverlayClick = true,
  className,
  maxWidth = 640,
  overlayZIndex = 1000,
  children,
}) => {
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && isOpen && !titleId && !ariaLabel) {
      // A dialog must have an accessible name (Q17).
      // eslint-disable-next-line no-console
      console.warn(
        "[AccessibleDialog] rendered without a dialog name — pass titleId or ariaLabel."
      );
    }
  }, [isOpen, titleId, ariaLabel]);

  // Resolve the app element at render time (not import time): in production
  // #root exists; in jsdom tests it may not, so we disable aria-hiding rather
  // than crash. Setting appElement to document.body would hide the modal itself.
  const appElement =
    typeof document !== "undefined" ? document.getElementById("root") : null;

  const customStyles = {
    overlay: {
      zIndex: overlayZIndex,
      backgroundColor: "rgba(8, 49, 39, 0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(5px)",
    },
    content: {
      opacity: 1,
      position: "static" as const,
      inset: "auto" as const,
      padding: "0px",
      display: "flex",
      flexDirection: "column" as const,
      borderRadius: "15px",
      border: 0,
      maxHeight: "94vh",
      width: "min(94vw, " + (typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth) + ")",
      maxWidth: "94vw",
      background: "#F3F6F6",
      overflow: "hidden" as const,
    },
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      onAfterOpen={() => initialFocusRef?.current?.focus()}
      appElement={appElement ?? undefined}
      ariaHideApp={!!appElement}
      style={customStyles}
      contentLabel={ariaLabel}
      aria={{
        labelledby: titleId,
        modal: true,
      }}
      shouldCloseOnEsc={shouldCloseOnEsc}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldReturnFocusAfterClose={true}
      shouldFocusAfterRender={true}
      preventScroll={true}
      closeTimeoutMS={10}
    >
      <div className={styles.dialog}>
        {showCloseButton && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={onRequestClose}
            aria-label={closeButtonLabel}
            title={closeButtonLabel}
          >
            <CloseModalSvg aria-hidden="true" />
          </button>
        )}
        <div className={className}>{children}</div>
      </div>
    </Modal>
  );
};

export default AccessibleDialog;
